import type {
  SiteLayer,
  SiteLayerEvidenceMeta,
  SiteLayerEvidenceUse,
} from '../../types/site';

const USE_RANK: Record<SiteLayerEvidenceUse, number> = {
  'map-only': 0,
  screening: 1,
  'site-truth': 2,
};

const LEGACY_USE_BY_SOURCE: Record<SiteLayer['source'], SiteLayerEvidenceUse> = {
  'kmz-import': 'site-truth',
  // User-authored geo uploads carry the same trust as KMZ: the operator
  // chose to import them as site data (the review step is the human gate).
  'shapefile-import': 'site-truth',
  'geojson-import': 'site-truth',
  'cad-import': 'site-truth',
  manual: 'site-truth',
  'arcgis-import': 'screening',
  'peeringdb-import': 'screening',
  'landgate-import': 'map-only',
  'doc-derived': 'map-only',
  'scenario-derived': 'map-only',
};

/**
 * Category ceilings prevent proposed/modelled geometry from being narrated as
 * an observed existing condition, regardless of its source-level default.
 */
function categoryCeiling(layer: SiteLayer): SiteLayerEvidenceUse {
  if (layer.category.startsWith('concept.') || layer.category.startsWith('impact.')) {
    return 'screening';
  }
  if (layer.source === 'landgate-import') return 'screening';
  if (layer.source === 'scenario-derived' || layer.source === 'doc-derived') return 'map-only';
  return 'site-truth';
}

/** Resolve explicit metadata or the non-destructive legacy default. */
export function effectiveSiteLayerUse(layer: SiteLayer): SiteLayerEvidenceUse {
  const requested = layer.evidence?.use ?? LEGACY_USE_BY_SOURCE[layer.source] ?? 'map-only';
  const ceiling = categoryCeiling(layer);
  return USE_RANK[requested] <= USE_RANK[ceiling] ? requested : ceiling;
}

export interface SiteLayerEvidencePartition {
  siteTruth: SiteLayer[];
  screening: SiteLayer[];
  mapOnly: SiteLayer[];
  hidden: SiteLayer[];
}

/** Partition layers once so prompts and SED compilation share one policy. */
export function partitionSiteLayersForEvidence(
  layers: ReadonlyArray<SiteLayer> | undefined,
): SiteLayerEvidencePartition {
  const partition: SiteLayerEvidencePartition = {
    siteTruth: [],
    screening: [],
    mapOnly: [],
    hidden: [],
  };
  for (const layer of layers ?? []) {
    if (layer.visible === false) {
      partition.hidden.push(layer);
      continue;
    }
    const use = effectiveSiteLayerUse(layer);
    if (use === 'site-truth') partition.siteTruth.push(layer);
    else if (use === 'screening') partition.screening.push(layer);
    else partition.mapOnly.push(layer);
  }
  return partition;
}

/** True when a legacy layer may have affected a report under the old policy. */
export function layerRequiresEvidenceRebuild(layer: SiteLayer): boolean {
  if (layer.visible === false) return false;
  return effectiveSiteLayerUse(layer) !== 'site-truth';
}

export function siteRequiresLayerEvidenceRebuild(
  layers: ReadonlyArray<SiteLayer> | undefined,
): boolean {
  return (layers ?? []).some(layerRequiresEvidenceRebuild);
}

/** Stamp explicit metadata on new imports without rewriting legacy rows. */
export function withSiteLayerEvidence(
  layer: SiteLayer,
  evidence: SiteLayerEvidenceMeta,
): SiteLayer {
  return { ...layer, evidence };
}
